import * as Units from '../../Units';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, ReferenceLine} from '../types';

export interface GrasslandChartOptions {
  /** Pre-computed cumulative degree-day data with `GDD` key */
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  height?: number;
  /** The GDD threshold value (default 200°C) */
  threshold?: number;
  /** Threshold label text (shown on horizontal line) */
  thresholdLabel?: string;
  /** Timestamp (unix seconds) when the threshold was reached */
  thresholdReachedTimestamp?: number;
  /** Formatted date label for the vertical line at the reached point */
  thresholdReachedLabel?: string;
  /** Translated series name for the tooltip (defaults to 'Growing Degree Days') */
  seriesName?: string;
}

export const grasslandChart = (options: GrasslandChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    height = 400,
    threshold = 200,
    thresholdLabel,
    thresholdReachedTimestamp,
    thresholdReachedLabel,
    seriesName,
  } = options;

  const referenceLines: ReferenceLine[] = [
    {
      type: 'horizontal',
      value: Units.convertDegreeDayValue(threshold, units.T) ?? threshold,
      color: theme.colors.secondary,
      dashArray: '5 3',
      lineWidth: 2,
      label: thresholdLabel,
      labelPosition: 'end',
    },
  ];

  if (thresholdReachedTimestamp != null) {
    referenceLines.push({
      type: 'vertical',
      value: thresholdReachedTimestamp,
      color: theme.colors.secondary,
      dashArray: '5 3',
      lineWidth: 2,
      label: thresholdReachedLabel,
      labelPosition: 'end',
    });
  }

  return {
    height,
    data,
    series: [
      {
        id: 'grassland-gdd',
        type: 'area',
        dataKey: 'GDD',
        name: seriesName ?? 'Growing Degree Days',
        color: theme.colors.primary,
        yAxisIndex: 0,
        interpolation: 'monotone',
        fillOpacity: 0.3,
        lineWidth: 2,
        showMarkers: false,
        zIndex: 1,
      },
    ],
    xAxis: createXAxisConfig({
      type: 'datetime',
      timezone,
      granularity: 'D',
      theme,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'gdd',
        theme,
        unitLabel: Units.getTemperatureUnitLabel(units),
        domain: [0, 'auto'],
        precision: 0,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity: 'D',
      metric: 'T',
      units,
      theme,
    }),
    legend: createLegendConfig(false, theme),
    grid: createGridConfig(theme),
    referenceLines,
    animate: true,
  };
};
